import { useState, useEffect, useCallback } from "react";

// App Bridge is loaded by the <script> tag in index.html (cdn.shopify.com/shopifycloud/app-bridge.js),
// which reads the API key from <meta name="shopify-api-key"> and the host from the URL, and exposes
// `window.shopify`. We deliberately do not bundle @shopify/app-bridge (v3): shipping a second copy
// meant two bridges initialising and a 100ms artificial delay before the first authenticated request.
interface ShopifyGlobal {
  idToken: () => Promise<string>;
}

declare global {
  interface Window {
    shopify?: ShopifyGlobal;
  }
}

interface AppBridgeState {
  error?: string;
  // Truthy once App Bridge is available; pages only use it as a readiness gate.
  app?: ShopifyGlobal;
}

export const useAppBridge = () => {
  const [state, setState] = useState<AppBridgeState>(() => ({
    app: window.shopify,
  }));

  useEffect(() => {
    if (state.app) return;
    // The CDN script is synchronous and normally defined before this bundle runs; poll briefly
    // in case it is blocked or slow rather than failing on the first render.
    let tries = 0;
    const id = setInterval(() => {
      if (window.shopify) {
        clearInterval(id);
        setState({ app: window.shopify });
      } else if (++tries >= 50) {
        clearInterval(id);
        setState({ error: "Shopify App Bridge failed to load" });
      }
    }, 100);
    return () => clearInterval(id);
  }, [state.app]);

  // Reads window.shopify at call time, so callers that captured this function on mount
  // (e.g. App.tsx's checkOnboarding retry loop) still recover once App Bridge is ready.
  const refreshSessionToken = useCallback(async (): Promise<string | null> => {
    const shopify = window.shopify;
    if (!shopify) {
      console.warn("[AppBridge] Cannot refresh token - App Bridge not loaded");
      return null;
    }

    try {
      return await shopify.idToken();
    } catch {
      setState((prev) => ({ ...prev, error: "Failed to refresh session token" }));
      return null;
    }
  }, []);

  return {
    ...state,
    refreshSessionToken,
  };
};
